import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { GoogleMap, GoogleMapProps } from '@react-google-maps/api'
import styled from 'styled-components'
import debounce from 'lodash.debounce'
import cn from 'clsx'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import useSearchMapResults from './useSearchMapResults'
import { EnsureGoogleMapsLoaded, useGoogleMapsContext } from 'contexts/GoogleMapsContext'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import usePrevious from 'hooks/usePrevious'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import {
  centerAndZoomOnMarker,
  centrePinForMobile,
  fitMarkersOnMap,
  getMarkersByOffers,
  getOfferId,
  getPadding,
  getPropertyId,
  isLoadedOffer,
  isMarkerWithinBounds,
  isOfferListMeta,
  mapHasAnyMarkersInView,
  parseBounds,
  parseOfferListBounds,
  subtractPaddingFromBounds,
  useVisitedMarkersState,
} from './utils'

import config from 'constants/config'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { ANYWHERE_PLACE_ID, BALI_LAT_LNG } from 'constants/search'
import HotelMapMarker from 'components/Pages/HotelSearchPage/HotelMapMarker/HotelMapMarker'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import TextButton from 'LuxKit/Button/TextButton'
import { sortBy, take, unique } from 'lib/array/arrayUtils'
import Snackbar from 'LuxKit/Snackbar/Snackbar'
import useOffers from 'hooks/Offers/useOffers'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import TripItemCardSingle from './TripPlanner/TripItemCardSingle'
import SearchMapMarker from './TripPlanner/SearchMapMarker'
import TripPlannerToggle from './TripPlanner/TripPlannerToggle'
import useTripPlannerPins from './useTripPlannerPins'
import uuidV4 from 'lib/string/uuidV4Utils'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import getOfferListKey from 'lib/offer/offerListKey'
import useStableObjectReference from 'hooks/useStableObjectReference'
import { useAppSelector } from '../../../hooks/reduxHooks'

const MapContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const FloatingBanner = styled.div`
  position: absolute;
  z-index: 1;
  left: 50%;
  transform: translateX(-50%);
  top: 32px;
`

const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  color: ${props => props.theme.palette.brand.primary.normal};
`

const FadableTripToggle = styled(TripPlannerToggle)`
  transition: opacity 0.2s;

  &.isInBackground {
    opacity: 0.7;
  }
`

export const TRIP_PLANNER_ALL_TRIPS_OPTION = 'All trips'
export const TRIP_PLANNER_HIDE_ALL_TRIPS_OPTION = 'Hide all trips'

export interface HoveredOffer {
  id: string
  type: string
}

interface Props {
  filters: App.OfferListFilters
  onBoundsChanged: (newBounds: google.maps.LatLngBounds) => void
  onSelectOffers: (offers: Array<App.Offer | App.BedbankOffer | App.OfferListMetaData>) => void
  onDeselectOffers: () => void
  onError: () => void
  onZeroResults: () => void
  selectedOfferIds: Array<string>
  handleProductClick?: (offer: App.Offer | App.OfferSummary | App.BedbankOffer | App.BedbankOfferSummary, position: number) => void
  onHover?: (offerId: string, type: string) => void
  rootRef?: React.RefObject<HTMLDivElement | null>
  offerListOverride?: App.OfferList
  /**
   * @deprecated This attribute is not to be used
   */
  fallbackOfferList?: App.OfferList
  pageSize?: number
  offerList?: App.OfferList
  fullScreenMap: boolean
  zeroResultsBanner: boolean
  showFloatingTotalCount?: boolean
  showOfferCardOnMapMarkerClick?: boolean
  hideTripPlannerToggle?: boolean
  /**
   * When true, prevents the map from updating its bounds/position.
   * Used during typeahead extraction to keep the map on the current location.
   */
  isSearchBlocked?: boolean
}

const MAP_OPTIONS: GoogleMapProps['options'] = {
  gestureHandling: 'greedy',
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  zoomControlOptions: {
    position: 9,
  },
  restriction: {
    latLngBounds: {
      north: 85,
      south: -85,
      west: -180,
      east: 180,
    },
    strictBounds: true,
  },
  minZoom: 3,
  maxZoom: 19,
  mapId: config.GOOGLE_MAP_ID,
  clickableIcons: false,
}

const MAP_STYLE = {
  height: '100%',
  width: '100%',
  flexGrow: 1,
}

const typeOrder: Record<string, number> = {
  hotel: 1,
  tactical_ao_hotel: 2,
  bedbank_hotel: 3,
}

const MARKER_DISPLAY_TIMEOUT = 2000

function isTrackableProduct(offer: App.Offer | App.BedbankOffer | App.OfferListMetaData): offer is App.Offer | App.BedbankOffer {
  return 'id' in offer
}

function SearchResultsMap({
  filters,
  fullScreenMap,
  handleProductClick,
  offerList,
  offerListOverride,
  onBoundsChanged,
  onDeselectOffers,
  onError,
  onHover,
  onSelectOffers,
  onZeroResults,
  pageSize,
  rootRef,
  selectedOfferIds,
  zeroResultsBanner,
  showFloatingTotalCount = true,
  showOfferCardOnMapMarkerClick = true,
  hideTripPlannerToggle = false,
  isSearchBlocked = false,
}: Props) {
  const {
    searchTargetLabel,
  } = useContext(GlobalSearchStateContext)

  const {
    isLoaded: isGoogleMapsLoaded,
  } = useGoogleMapsContext()

  const searchKey = getOfferListKey(filters)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  const [hoveredOffer, setHoveredOffer] = useState<HoveredOffer | undefined>(undefined)
  const [recentSearchResults, setRecentSearchResults] = useState<Array<Array<App.HotelOffer | App.BedbankOffer | App.HotelOfferSummary | App.BedbankOfferSummary | App.OfferListMetaData>>>([])
  const [markers, setMarkers] = useState<Array<App.SearchMapMarker>>([])
  const [map, setMap] = useState<google.maps.Map | null>(null)

  const setSearchLabel = useCallback((searchLabel: string) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_TARGET_LABEL, searchTargetLabel: searchLabel })
  }, [globalSearchDispatch])

  const currentSearchResult = useSearchMapResults(
    selectedOfferIds,
    offerListOverride,
    pageSize,
    offerList,
  )

  const { allOffersLoaded, fetchingPage } = currentSearchResult
  const currentOfferListKey = currentSearchResult.key || uuidV4()

  const [isProjectionLoaded, setIsProjectionLoaded] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  /*
    Normally when the map goes idle, it means the user has manually panned/zoomed/rotated,
    and we should do a new bounds search.
    But it also happens when the map loads, or when the map bounds are moved programmatically,
    or when the map size changes due to resizing the window or dismissing a notification.
    We can distinguish between these scenarios with a combination of:
    - Tracking whether the map has been panned/zoomed/rotated, via `hasInteracted` ref
    - Explicitly ignoring the next `onIdle` event, via `ignoreNextIdle` ref
    Neither method on its own is quite enough to handle all edge cases.
    Ideally the map would have a built-in callback for this, but it doesn't.
  */
  const ignoreNextIdle = useRef<boolean>(true)
  const hasInteracted = useRef<boolean>(false)
  const nativeControlListener = useRef<google.maps.MapsEventListener | null>(null)
  const hasAttachedNativeListener = useRef<boolean>(false)
  const lastProgrammaticMoveTime = useRef<number>(0)

  const PROGRAMMATIC_MOVE_GRACE_PERIOD = 300

  const isWithinProgrammaticMoveWindow = useCallback(() => {
    const timeSinceLastMove = Date.now() - lastProgrammaticMoveTime.current
    return timeSinceLastMove < PROGRAMMATIC_MOVE_GRACE_PERIOD
  }, [])

  const onInteract = useCallback(() => {
    if (!isWithinProgrammaticMoveWindow()) {
      ignoreNextIdle.current = false
      hasInteracted.current = true
    }
  }, [isWithinProgrammaticMoveWindow])

  const removeNativeControlListener = useCallback(() => {
    if (nativeControlListener.current) {
      google.maps.event.removeListener(nativeControlListener.current)
      nativeControlListener.current = null
    }
  }, [])

  // Check if this is a new destination or property search, in which case we need to update the map position
  const prevAllOffersLoaded = usePrevious(allOffersLoaded, false)
  const prevFilters = usePrevious(filters, {})
  const prevOfferListKey = usePrevious(currentOfferListKey)
  const isNewlyLoaded = allOffersLoaded && (currentOfferListKey !== prevOfferListKey || !prevAllOffersLoaded) && !fetchingPage
  const isNewDestination = !!filters.destinationId && filters.destinationId !== prevFilters.destinationId
  const isNewProperty = !!filters.propertyId && filters.propertyId !== prevFilters.propertyId
  const isNewLandmark = !!filters.landmarkId && filters.landmarkId !== prevFilters.landmarkId
  const isNewNaturalLanguageQuery = !!filters.query && filters.query !== prevFilters.query
  const hasDates = !!(filters.checkIn && filters.checkOut)
  const isNewDates = hasDates && (filters.checkIn !== prevFilters.checkIn || filters.checkOut !== prevFilters.checkOut)
  const isNewRooms = JSON.stringify(filters.rooms) !== JSON.stringify(prevFilters.rooms)
  const isNewBounds = !!filters.bounds && filters.bounds !== prevFilters.bounds
  const isNewHolidayTypes = JSON.stringify(filters.holidayTypes) !== JSON.stringify(prevFilters.holidayTypes)
  const isNewAmenities = JSON.stringify(filters.amenities) !== JSON.stringify(prevFilters.amenities)
  // we distinguish between when bounds are changed vs when filters are changed, because for the former we can still show previous results
  const isNewManualSearch = isNewDestination || isNewProperty || isNewDates || isNewRooms || isNewHolidayTypes || isNewAmenities
  const fetchingList = (offerList?.offerIds?.length || 0) < (offerListOverride?.offerCount || 0)

  const currentFilters = useOfferListFilters(filters, { disabled: true })

  const isNewSearch = isNewManualSearch || isNewBounds
  const lastSearch = useRef({
    destination: false,
    property: false,
    landmark: false,
    query: false,
    bounds: false,
    dates: false,
    rooms: false,
  })
  if (isNewSearch) {
    lastSearch.current = {
      destination: isNewDestination,
      property: isNewProperty,
      landmark: isNewLandmark,
      query: isNewNaturalLanguageQuery,
      bounds: isNewBounds,
      dates: isNewDates,
      rooms: isNewRooms,
    }
  }

  const [markerDisplayTimedOut, setMarkerDisplayTimedOut] = useState(false)

  useEffect(() => {
    setMarkerDisplayTimedOut(false)
    const timeoutId = setTimeout(() => {
      setMarkerDisplayTimedOut(true)
    }, MARKER_DISPLAY_TIMEOUT)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [searchKey])

  useEffect(() => {
    if (!offerList?.fetching && !currentSearchResult.fetchingPage) {
      setRecentSearchResults([currentSearchResult.offers])
    }
  }, [offerList?.fetching, currentSearchResult.fetchingPage, currentSearchResult.offers])

  const stableFilters = useStableObjectReference(filters)
  const previousFilters = usePrevious(stableFilters)
  const [waitingForOffersAfterFilterChange, setWaitingForOffersAfterFilterChange] = useState(false)

  useEffect(() => {
    const filtersChanged = previousFilters?.destinationId !== filters.destinationId ||
      previousFilters?.propertyId !== filters.propertyId ||
      previousFilters?.landmarkId !== filters.landmarkId ||
      previousFilters?.query !== filters.query

    if (filtersChanged) {
      setWaitingForOffersAfterFilterChange(true)
    }
  }, [filters.destinationId, filters.propertyId, filters.landmarkId, previousFilters, filters.query])

  useEffect(() => {
    if (markerDisplayTimedOut) {
      if (waitingForOffersAfterFilterChange && currentSearchResult.offers.length > 0) {
        setRecentSearchResults([take(currentSearchResult.offers, pageSize ?? currentSearchResult.offers.length)])
        setWaitingForOffersAfterFilterChange(false)
        return
      }

      // Only combine offers if we're not waiting for offers after a filter change
      if (!waitingForOffersAfterFilterChange) {
        const currentRecentSearchResults =
          recentSearchResults.length > 0 ? recentSearchResults[0] : []
        const nextRecentSearchResults = currentSearchResult.offers
        const combinedRecentSearchResults =
          unique([...nextRecentSearchResults, ...currentRecentSearchResults])

        setRecentSearchResults([take(combinedRecentSearchResults, pageSize ?? combinedRecentSearchResults.length)])
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSearchResult.offers, markerDisplayTimedOut, waitingForOffersAfterFilterChange])

  useEffect(() => {
    if (currentSearchResult.error) {
      onError()
    }
  }, [currentSearchResult.error, onError])

  const currentOffers = useMemo(() => {
    const [_kind, id] = filters.propertyId?.split(':') || []
    return sortBy(recentSearchResults.flat().filter(offer => !('isSoldOut' in offer) || !offer.isSoldOut || (id && offer.property?.id === id)),
      value => typeOrder[value.type ?? ''],
      'asc',
    )
  }, [filters.propertyId, recentSearchResults])

  const loadedOffers = useMemo(() => currentOffers.filter(isLoadedOffer), [currentOffers])
  const currentOffersBestPrices = useOffersBestPrices(loadedOffers, filters)
  const currentBounds = useMemo(() => isGoogleMapsLoaded && filters.bounds !== undefined ? parseBounds(filters.bounds) : undefined, [filters.bounds, isGoogleMapsLoaded])

  const offerMetaDataState = useAppSelector(state => state.offer.searchResultMetadata.offerMetaData)

  const isOfferUnavailable = useCallback((offer: App.AnyOffer | App.OfferListMetaData): boolean => {
    const offerMetaDataKey = getOfferListKey(filters)
    const offerId = isOfferListMeta(offer) ? offer.offerId : offer.id

    const offerMetaData = offerMetaDataState[offerMetaDataKey]?.[offerId]

    if (offerMetaData?.available === false) {
      return true
    }

    return currentOffersBestPrices[getOfferId(offer)]?.soldOut ?? false
  }, [currentOffersBestPrices, filters, offerMetaDataState])

  const currentMarkers = useMemo(() => {
    const offerMarkers = getMarkersByOffers(currentOffers, true)
    const [_kind, filteredPropertyId] = filters.propertyId?.split(':') || []
    const displayOfferMarkers = offerMarkers.filter(
      (marker) => !marker.offers.every(
        (offer) => {
          const isSoldOut = isOfferUnavailable(offer)
          const offerPropertyId = getPropertyId(offer)
          // Allow sold out offers if they match the filtered property ID
          return isSoldOut && (!filteredPropertyId || offerPropertyId !== filteredPropertyId)
        },
      ),
    ) // filter out, sold out offers (except for filtered property)
    const markerResult = currentBounds ? displayOfferMarkers.filter((marker) => isMarkerWithinBounds(marker, currentBounds)) : displayOfferMarkers
    const limit = config.search.mapView.pinLimit + config.search.mapView.additionalPins
    if (limit) {
      return take(markerResult, limit)
    }
    return markerResult
  }, [currentBounds, currentOffers, filters.propertyId, isOfferUnavailable])

  useEffect(() => {
    setMarkers(currentMarkers)
  }, [currentMarkers])

  const activeMarkerId = selectedOfferIds.join(',')
  const [visitedMarkerIds, addVisitedMarkerId] = useVisitedMarkersState()

  useEffect(() => {
    if (activeMarkerId) {
      addVisitedMarkerId(activeMarkerId)
    }
  }, [activeMarkerId, addVisitedMarkerId])

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  const [centrePin, setCentrePin] = useState(true)
  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const findSelectedMarker = useCallback((markers: Array<App.SearchMapMarker>, selectedId: string) =>
    markers.find(marker => marker.offers.some(offer => getOfferId(offer) === selectedId)),
  [])

  const handleMobilePinCentering = useCallback((clickedMarker?: App.SearchMapMarker) => {
    // Early returns for invalid states
    if (isTabletScreenSizeAndUp || !map) return
    if (!clickedMarker && !centrePin) return

    // Use clicked marker or find selected marker
    const markerToCenter = clickedMarker || findSelectedMarker(markers, selectedOfferIds[0])

    if (markerToCenter) {
      centrePinForMobile(map, markerToCenter)
    }
  }, [isTabletScreenSizeAndUp, map, centrePin, findSelectedMarker, markers, selectedOfferIds])

  const onMarkerClick = useCallback((marker: App.SearchMapMarker, pinType: string, e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setCentrePin(true)
    handleMobilePinCentering(marker)
    onSelectOffers(marker.offers)

    const offer = marker.offers[0]
    if (isTrackableProduct(offer)) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer,
        position: 0,
      })
    }

    if (e) {
      e.stopPropagation()
    }
  }, [handleMobilePinCentering, onSelectOffers, dispatchOfferListEvent])

  // Determine whether to report zero results
  const hadMarkersLoadedOrInViewAtLastLoad = useRef(false)
  useEffect(() => {
    if (isNewlyLoaded) {
      const offersLoadedOrInView = currentSearchResult.totalNumOffers > 0 || mapHasAnyMarkersInView(map, markers)
      if (
        !offersLoadedOrInView &&
        !currentSearchResult.error &&
        hadMarkersLoadedOrInViewAtLastLoad.current
      ) {
        onZeroResults()
      }
      hadMarkersLoadedOrInViewAtLastLoad.current = offersLoadedOrInView
    }
  }, [currentSearchResult, isNewlyLoaded, map, markers, onZeroResults])

  // If user searched for a property, select it. If user searched for a destination, deselect offers
  useEffect(() => {
    if (isNewlyLoaded) {
      if (!filters.destinationId && searchTargetLabel !== 'Map area') {
        setSearchLabel('Map area')
      }
      if (lastSearch.current.property && filters.propertyId) {
        const [_kind, id] = filters.propertyId.split(':')
        const marker = markers.find(
          marker => marker.offers.some(
            offer => 'property' in offer && offer.property?.id === id,
          ),
        )
        if (marker) {
          onMarkerClick(marker, 'pin')
        }
      }
    }
  }, [isNewlyLoaded, markers, selectedOfferIds.length, filters.propertyId, onMarkerClick, onDeselectOffers, filters.destinationId, setSearchLabel, searchTargetLabel])

  const {
    tripPlannerLEItemOfferIds,
    dropDownVisible,
    deselectTripItem,
    LEandCustomTripPlannerMarkers,
    canShowTripPlannerToggle,
    handleTripPlannerItemSelect,
    updateTripPlannerMarkers,
    selectedTrip,
    selectedTripItem,
    handleSelectTrip,
    trips,
    toggleDropdownVisibility,
    tripItemCardSingleRef,
  } = useTripPlannerPins({
    map,
    markers,
    onDeselectOffers,
  })

  const destinationBounds = useMemo(() => {
    if (!map) return undefined
    return offerList?.bounds ? parseOfferListBounds(offerList.bounds) : undefined
  }, [offerList?.bounds, map])
  // Fit the correct bounds on load
  const onLoad = useCallback((map: google.maps.Map) => {
    ignoreNextIdle.current = true
    lastProgrammaticMoveTime.current = Date.now()
    if (filters.destinationId === ANYWHERE_PLACE_ID) {
      map.setCenter(BALI_LAT_LNG)
      map.setZoom(2)
    } else if (filters.propertyId) {
      const [_kind, id] = filters.propertyId.split(':')
      const marker = markers.find(
        marker => marker.offers.some(
          offer => 'property' in offer && offer.property?.id === id,
        ),
      )
      if (marker) {
        centerAndZoomOnMarker(map, marker, true)
      } else {
        fitMarkersOnMap(map, markers)
      }
    } else if (destinationBounds) {
      map.fitBounds(destinationBounds)
    }
    else if (filters.bounds && currentBounds) {
      map.fitBounds(currentBounds, getPadding(true))
    } else {
      if (!selectedOfferIds.length && markers.length) {
        fitMarkersOnMap(map, markers)
      }
    }
    setMap(map)
  }, [currentBounds, filters.bounds, filters.destinationId, filters.propertyId, markers, selectedOfferIds, destinationBounds])

  // Update the bounds if the markers have changed
  useEffect(() => {
    if (map &&
      !isSearchBlocked &&
      (!fetchingPage || markerDisplayTimedOut) &&
      (
        lastSearch.current.destination ||
        lastSearch.current.property ||
        lastSearch.current.landmark ||
        lastSearch.current.query
      )
    ) {
      ignoreNextIdle.current = true
      lastProgrammaticMoveTime.current = Date.now()

      if (lastSearch.current.property && filters.propertyId) {
        const [_kind, id] = filters.propertyId.split(':')
        const marker = markers.find(
          marker => marker.offers.some(
            offer => 'property' in offer && offer.property?.id === id,
          ),
        )
        if (marker) {
          centerAndZoomOnMarker(map, marker, true)
          handleMobilePinCentering()
          return
        }
      }
      if (filters.destinationId === ANYWHERE_PLACE_ID) {
        map.setCenter(BALI_LAT_LNG)
        map.setZoom(2)
      } else if (destinationBounds) {
        map.fitBounds(destinationBounds)
        handleMobilePinCentering()
      } else {
        fitMarkersOnMap(map, markers)
        handleMobilePinCentering()
      }
    }
  }, [filters.destinationId, filters.propertyId, filters.landmarkId, fetchingPage, map, markers, selectedOfferIds.length, handleMobilePinCentering, markerDisplayTimedOut, destinationBounds, isSearchBlocked])

  useEffect(() => {
    if (!map || fetchingPage || hasAttachedNativeListener.current) return

    nativeControlListener.current = google.maps.event.addListener(
      map,
      'bounds_changed',
      () => {
        if (!ignoreNextIdle.current && !isWithinProgrammaticMoveWindow()) {
          hasInteracted.current = true
        }
      },
    )

    hasAttachedNativeListener.current = true

    return removeNativeControlListener
  }, [map, fetchingPage, removeNativeControlListener, isWithinProgrammaticMoveWindow])

  const onUnmount = useCallback((_map: google.maps.Map) => {
    removeNativeControlListener()
    hasAttachedNativeListener.current = false
    setMap(null)
  }, [removeNativeControlListener])

  // Maintain mapping of `google.maps.Marker` instances to the source `App.SearchMapMarker` objects
  // so that the clusterer calculator can figure out what offers are in a cluster
  const markerMap = useRef(new Map<google.maps.Marker, App.SearchMapMarker>())
  const onMarkerLoad = useCallback((markerInstance: google.maps.Marker, markerData: App.SearchMapMarker) => {
    markerMap.current.set(markerInstance, markerData)
  }, [])
  const onMarkerUnmount = useCallback((markerInstance: google.maps.Marker) => {
    markerMap.current.delete(markerInstance)
  }, [])

  const onUserMovedMap = useMemo<() => void>(() => debounce(
    () => {
      setCentrePin(false)
      const mapBounds = map?.getBounds()
      const boundingRect = containerRef.current?.getBoundingClientRect()
      if (mapBounds && boundingRect) {
        const boundsWithoutMargin = subtractPaddingFromBounds(mapBounds, boundingRect)
        onBoundsChanged(boundsWithoutMargin)
        updateTripPlannerMarkers(mapBounds)
      }
      // Reset after search executes to prevent duplicate searches from subsequent onIdle events
      hasInteracted.current = false
    },
    2000,
    { leading: true, trailing: true },
  ), [map, onBoundsChanged, updateTripPlannerMarkers])

  const onIdle = useCallback(() => {
    const shouldTrigger = !ignoreNextIdle.current && hasInteracted.current
    if (shouldTrigger) {
      onUserMovedMap()
    }

    ignoreNextIdle.current = false
    if (!shouldTrigger) {
      hasInteracted.current = false
    }
  }, [onUserMovedMap])

  const isMobile = useScreenSizeOnly('mobile')
  const mapOptions = useMemo(() => ({
    ...MAP_OPTIONS,
    minZoom: isMobile ? 2 : MAP_OPTIONS?.minZoom,
    zoomControl: !isMobile,
    disableDoubleClickZoom: !!activeMarkerId,
    zoomControlOptions: {
      position: 3, // Top right zoom controls
    },
  }), [activeMarkerId, isMobile])

  useEffect(() => {
    const root = rootRef?.current
    if (root) {
      const handleOfferHover = (event: CustomEvent) => {
        const id = event.detail.id
        const offerType = event.detail.type
        const persist = event.detail.persist

        if (!id && persist) return

        if (!id && !persist) {
          setHoveredOffer(undefined)
          return
        }

        setHoveredOffer({ id, type: offerType })
      }
      root.addEventListener('offerHover', handleOfferHover)
      return () => {
        root.removeEventListener('offerHover', handleOfferHover)
      }
    }
  }, [rootRef])

  // projection is only loaded after map is fully initialised
  useEffect(() => {
    if (!map) {
      return
    }
    google.maps.event.addListenerOnce(map, 'projection_changed', function() {
      setIsProjectionLoaded(true)
    })
  }, [map])

  const { singleMarkers } = useMemo(() => {
    if (!markers.length || !map || !isProjectionLoaded) {
      return {}
    }

    return { singleMarkers: markers }
  }, [isProjectionLoaded, map, markers])

  const singleMarkerOfferIds = singleMarkers?.map((marker) => (
    marker.id
  )) ?? []

  // fetch offer details for only the markers that can be clicked
  useOffers(singleMarkerOfferIds)

  function mapClick() {
    onDeselectOffers()
    setHoveredOffer(undefined)
  }

  return (
    <>
      <MapContainer ref={containerRef}>
        <EnsureGoogleMapsLoaded loadingElement={<LoadingIndicator inline />}>
          {!isGoogleMapsLoaded && <LoadingIndicator inline />}
          {isGoogleMapsLoaded && (
            <GoogleMap
              id="le-map"
              zoom={17}
              onLoad={onLoad}
              onUnmount={onUnmount}
              onClick={mapClick}
              onDrag={onInteract}
              onZoomChanged={onInteract}
              onIdle={onIdle}
              mapContainerStyle={MAP_STYLE}
              options={mapOptions}
            >
              {/* The order of elements defines their z-axis stacking. Former elements are below latter elements. */}
              {
                canShowTripPlannerToggle && !hideTripPlannerToggle && (
                  <>
                    {
                      (selectedTrip.name !== TRIP_PLANNER_HIDE_ALL_TRIPS_OPTION || selectedTrip.tripId) &&
                      LEandCustomTripPlannerMarkers.map((marker) => {
                        return (
                          <SearchMapMarker
                            key={`tripplanner-marker-${marker.item.id}-${marker.itemPlaceType}`}
                            item={marker.item}
                            position={marker.position}
                            active={selectedTripItem?.id === marker.item.id}
                            onSelect={handleTripPlannerItemSelect}
                            onDeselect={deselectTripItem}
                          />
                        )
                      })
                    }
                    <FadableTripToggle
                      trips={trips}
                      handleSelectTrip={handleSelectTrip}
                      selectedTrip={selectedTrip}
                      dropDownVisible={dropDownVisible && !selectedTripItem}
                      toggleDropdownVisibility={toggleDropdownVisibility}
                      className={cn({ isInBackground: !!selectedTripItem })}
                    />
                    {
                      selectedTripItem && (
                        <TripItemCardSingle
                          cardRef={tripItemCardSingleRef}
                          selectedItem={selectedTripItem}
                          onDeselect={deselectTripItem}
                        />
                      )
                    }
                  </>
                )
              }

              {markers.map((marker, index) => {
                const isSoldOut = marker.offers.every(
                  (offer) => isOfferUnavailable(offer),
                )

                return <HotelMapMarker
                  key={marker.id}
                  marker={marker}
                  index={index}
                  onMarkerClick={onMarkerClick}
                  onMarkerLoad={onMarkerLoad}
                  onMarkerUnmount={onMarkerUnmount}
                  isActive={activeMarkerId === marker.id}
                  isHovered={hoveredOffer?.id === marker.id}
                  isVisited={visitedMarkerIds.has(marker.id)}
                  filters={filters}
                  onDeselectOffers={onDeselectOffers}
                  handleProductClick={handleProductClick}
                  onHover={onHover}
                  soldOut={isSoldOut}
                  isSaved={tripPlannerLEItemOfferIds.has(getOfferId(marker.offers[0]))}
                  map={map}
                  animateMarker
                  showOfferViewWhenActive={showOfferCardOnMapMarkerClick}
                />
              })}
            </GoogleMap>
          )}

          {(offerList?.fetching || currentSearchResult.fetchingPage || isSearchBlocked) && (
            <FloatingBanner>
              <TextButton
                kind="secondary"
                variant="dark"
                nonInteractable={!allOffersLoaded}
                endIcon={<ColourfulLoadingAnimation size={8} />}
              >
                Loading offers
              </TextButton>
            </FloatingBanner>
          )}

          {(!offerList?.fetching && !currentSearchResult.fetchingPage && !isSearchBlocked && markers.length > 0 && showFloatingTotalCount) && (
            <FloatingBanner>
              <TextButton
                kind="secondary"
                variant="dark"
                nonInteractable={!allOffersLoaded}
              >
                Showing {markers.length} of {currentFilters.filters?.total}
              </TextButton>
            </FloatingBanner>
          )}
        </EnsureGoogleMapsLoaded>
      </MapContainer>
      {zeroResultsBanner && fullScreenMap && !fetchingList && currentSearchResult.noOffersAvailable && (
        <FloatingBanner>
          <Snackbar
            description="No offers found in this area"
            kind="neutral"
          />
        </FloatingBanner>
      )}
    </>
  )
}

export default React.memo(SearchResultsMap)
